import { Box, Card, Typography, styled, useTheme } from '@mui/material'
import React from 'react'

export interface ReportTemplateSummaryCardProps {
  label: string
  count: number
  icon: React.ReactNode
  accentColor: string
  dotColor: string
  isSelected: boolean
  hasActiveSelection: boolean
  onClick: () => void
}

export const ReportTemplateSummaryCard: React.FC<ReportTemplateSummaryCardProps> = ({
  label,
  count,
  icon,
  accentColor,
  dotColor,
  isSelected,
  hasActiveSelection,
  onClick,
}) => {
  const theme = useTheme()
  return (
    <StyledSummaryCard
      isSelected={isSelected}
      accentColor={accentColor}
      hasActiveSelection={hasActiveSelection}
      onClick={onClick}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
        <Box display="flex" alignItems="center" gap={1}>
          <DotIndicator dotColor={dotColor} />
          <Typography
            variant="caption"
            fontWeight={600}
            letterSpacing="0.05em"
            sx={{
              color: accentColor,
              textTransform: 'uppercase',
              fontSize: '14px',
            }}
          >
            {label}
          </Typography>
        </Box>

        <IconWrapper accentColor={accentColor}>{icon}</IconWrapper>
      </Box>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="h4" fontWeight={700} color={theme.palette.text.primary} lineHeight={1}>
          {count}
        </Typography>
      </Box>
    </StyledSummaryCard>
  )
}

const StyledSummaryCard = styled(Card, {
  shouldForwardProp: prop => prop !== 'isSelected' && prop !== 'accentColor' && prop !== 'hasActiveSelection',
})<{ isSelected: boolean; accentColor: string; hasActiveSelection: boolean }>(
  ({ theme, isSelected, accentColor, hasActiveSelection }) => ({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    padding: theme.spacing(1.5, 2),
    borderRadius: '8px',
    cursor: 'pointer',
    borderTop: `3px solid ${accentColor}`,
    position: 'relative',
    zIndex: isSelected ? 1 : 0,
    boxShadow: isSelected ? `0 0 0 2px ${accentColor}, 0 6px 20px ${accentColor}30` : '0px 1px 3px rgba(0,0,0,0.08)',
    backgroundColor: isSelected ? `${accentColor}35` : `${accentColor}15`,
    opacity: hasActiveSelection && !isSelected ? 0.6 : 1,
    transform: isSelected ? 'translateY(-3px)' : 'translateY(0)',
    transition: 'box-shadow 0.25s, background-color 0.25s, opacity 0.25s, transform 0.25s',
    '&:hover': {
      boxShadow: `0 0 0 2px ${accentColor}, 0 6px 20px ${accentColor}30`,
      backgroundColor: isSelected ? `${accentColor}45` : `${accentColor}28`,
      opacity: 1,
      transform: 'translateY(-3px)',
    },
    '&:active': {
      backgroundColor: `${accentColor}45`,
    },
  }),
)

const IconWrapper = styled(Box)<{ accentColor: string }>(({ accentColor }) => ({
  width: '36px',
  height: '36px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: `${accentColor}18`,
  color: accentColor,
  fontSize: '20px',
  flexShrink: 0,
}))

const DotIndicator = styled(Box)<{ dotColor: string }>(({ dotColor }) => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: dotColor,
  flexShrink: 0,
}))
